import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type VoicePlaybackIssue = {
    voicePlaybackIssueId: string;
    userId: string;
    userEmail?: string | null;
    userFullName?: string | null;
    examTaskId: string;
    questionNo?: string | null;
    displayTitle?: string | null;
    examName?: string | null;
    examTaskType?: string | null;
    preferredVoiceLabel: string;
    fallbackVoiceLabel?: string | null;
    availableVoiceLabelsJson: string;
    occurrenceCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
};

export type VoicePlaybackIssueSummary = {
    totalIssues: number;
    totalOccurrences: number;
    affectedQuestions: number;
    affectedUsers: number;
};

export type VoicePlaybackIssuePage = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: VoicePlaybackIssue[];
    summary: VoicePlaybackIssueSummary;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    errorMessage?: string;
    message?: string;
    data?: T;
    result?: T;
};

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = String(
        import.meta.env.VITE_API_BASE_URL ??
            import.meta.env.VITE_API_URL ??
            settings.adminApiBaseUrl ??
            'https://test-api.pteultra.com/api/v1',
    ).replace(/\/+$/g, '');
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );
    return { baseUrl, apiVersion, adminToken };
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v\d+(?:\.\d+)?\/?$/i.test(value);
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (payload && typeof payload === 'object') {
        const envelope = payload as AdminApiEnvelope<T>;
        if (envelope.success === false) {
            throw new Error(envelope.errorMessage || envelope.message || 'Could not load voice playback issues.');
        }
        if (envelope.data !== undefined) return envelope.data;
        if (envelope.result !== undefined) return envelope.result;
    }
    return payload as T;
}

export async function getVoicePlaybackIssues(params: {
    page: number;
    pageSize: number;
    search?: string;
}): Promise<VoicePlaybackIssuePage> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminVoicePlaybackIssues`);
    url.searchParams.set('page', String(params.page));
    url.searchParams.set('pageSize', String(params.pageSize));
    if (params.search?.trim()) url.searchParams.set('search', params.search.trim());

    const headers = new Headers({ Accept: 'application/json' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);

    const response = await fetch(url.toString(), { headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) {
        throw new Error(typeof payload === 'string' && payload.trim() ? payload : `${response.status} ${response.statusText}`);
    }
    return unwrap(payload as VoicePlaybackIssuePage | AdminApiEnvelope<VoicePlaybackIssuePage>);
}
